import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router";
import {
  X,
  Mail,
  Phone,
  UserCircle2,
  CalendarDays,
  IdCard,
  Clock,
  Copy,
  ChevronRight,
  Briefcase,
  Monitor,
  Search,
  FileText,
  User,
  MailCheck,
  PhoneCall,
  MessageSquare,
  Tag,
  ShoppingBag,
  Package,
} from "lucide-react";
import { useApiCall } from "../../../../component/custom-hooks/api-call";
import InnerLoader from "../../../../component/ui/loader/inner-loader";
import Chip from "../../../../component/ui/chip";
import CommentsSection from "../../../../component/comment";
import { Alert, Snackbar } from "@mui/material";
import InvoiceQuickView from "../../../../component/invoice/InvoiceQuickView";

const tabs = [
  { key: "summary", label: "Summary" },
  { key: "deal", label: "Deal" },
  { key: "transactions", label: "Transactions" },
  { key: "comments", label: "Comments" },
  { key: "call", label: "Calls" },
  { key: "emails", label: "Emails" },
  { key: "sms", label: "Sms" },
];

function classNames(...xs) {
  return xs.filter(Boolean).join(" ");
}

function Badge({ children, tone = "default", icon: Icon }) {
  const tones = {
    default: "bg-slate-100 text-slate-700",
    green: "bg-emerald-100 text-emerald-700",
    blue: "bg-blue-100 text-blue-700",
    amber: "bg-amber-100 text-amber-800",
    red: "bg-rose-100 text-rose-700",
    grey: "bg-neutral-100 text-neutral-700",
  };
  return (
    <span
      className={classNames(
        "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium",
        tones[tone]
      )}
    >
      {Icon ? <Icon size={14} /> : null}
      {children}
    </span>
  );
}

function Stat({ title, value, icon: Icon, hint, tone = "default" }) {
  return (
    <div className="group relative rounded-2xl border border-solid border-slate-200 bg-white p-4 shadow-sm hover:shadow-md">
      <div className="flex items-start gap-3">
        <div className="min-w-0">
          <div className="text-xs text-slate-500 capitalize">
            {title.includes("_")
              ? title.split("_").map((word) => ` ${word}`)
              : title}
          </div>
          {/* truncate */}
          <div className="text-lg font-semibold tracking-tight">{value}</div>
          {hint && <div className="mt-1 text-xs text-slate-400">{hint}</div>}
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  icon: Icon,
  copyable,
  link,
  endTag = false,
  LastElement,
}) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="grid grid-cols-12 items-center gap-2 rounded-xl border border-solid border-slate-200 bg-white p-3 transition hover:bg-slate-50">
      <div className="col-span-4 flex items-center gap-2 text-[13px] font-medium text-slate-500">
        {Icon ? <Icon className="size-4" /> : null}
        {label}
      </div>
      <div className="col-span-8 flex items-center justify-between gap-2 text-[13px] text-slate-800">
        {link ? (
          <a
            href={link}
            className="truncate underline decoration-slate-300 underline-offset-2 hover:text-blue-600 hover:decoration-blue-400"
          >
            {value}
          </a>
        ) : (
          <span className="truncate">{value}</span>
        )}
        {copyable || endTag ? (
          copyable ? (
            <button
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(String(value ?? ""));
                  setCopied(true);
                  setTimeout(() => setCopied(false), 1500);
                } catch {}
              }}
              className="inline-flex items-center gap-1 rounded-md border border-solid border-slate-200 bg-white px-2 py-1 text-[11px] text-slate-600 hover:bg-slate-100"
            >
              <Copy className="size-3.5" /> {copied ? "Copied" : "Copy"}
            </button>
          ) : (
            <LastElement />
          )
        ) : null}
      </div>
    </div>
  );
}

function normalizeToArray(value) {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
}

export default function ProjectQuickView({
  open = true,
  onClose = () => {},
  data,
}) {
  const [lead, setLead] = useState({});
  const [activeTab, setActiveTab] = useState("summary");
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState(lead?.leadstatus);
  const containerRef = useRef(null);
  const apiCall = useApiCall();
  const [isLoading, setIsLoading] = useState(false);

  const [invoicePopup, setInvoicePopup] = useState(false);
  const [invoiceData, setInvoiceData] = useState(null);

  const [contact, setcontact] = useState({});
  const [alertData, setAlertData] = useState({ isShow: false });
  const [changeModal, setChangeModal] = useState(false);

  const GetData = async (key) => {
    let apiData = JSON.stringify({
      projectkey: key,
    });

    try {
      let call = await apiCall({
        method: "get",
        url: `projects/get-project/${key}`,
        data: apiData,
      });
      let projectData = call?.project;

      console.log("ProjectData", projectData);
      setIsLoading(true);

      let activity = projectData?.recent_activity?.map((item) => {
        const dateObj = new Date(item.time);
        const formattedDate = dateObj
          .toISOString()
          .slice(0, 16)
          .replace("T", " ");
        // This gives "YYYY-MM-DD HH:MM"

        return {
          ts: formattedDate,
          text: item.data,
        };
      });

      const sdata = {
        id: projectData?.projectid,
        invoiceId: projectData?.invoiceid,
        status: projectData?.status,
        payment: projectData?.payment,
        cost: projectData?.projectcost,
        start: projectData?.startdate,
        end: projectData?.enddate,
        brief: projectData?.projectbrief,
        rush: projectData?.rush,
        createdAt: projectData?.created_at,
        updatedAt: projectData?.updated_at,

        customer: projectData?.customer_details,
        agent: projectData?.agent_details,
        support: projectData?.support_details,
        transactions: projectData?.transactions,
        comments: projectData?.comments,
        stats: projectData?.stats ?? [],
        invoice_details: projectData?.invoice_details ?? [],
        project_deals: projectData?.project_deals ?? [],
        package_details: projectData?.package_details ?? [],
      };

      setStatus(projectData?.status);
      setLead(sdata);
      setIsLoading(true);
    } catch (error) {
      console.warn("Error : ", error);
    }
  };

  // useOnClickOutside(containerRef, () => onClose());

  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose();
      if (e.key.toLowerCase() === "f" && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        const el = document.getElementById("quickview-search");
        el?.focus();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const onSuccess = (message) => {
    setAlertData({ isShow: true, type: "success", message: message });
    GetData(contact?.id);
    setChangeModal(false);
  };

  const onError = (message) => {
    setAlertData({ isShow: true, type: "error", message: message });
  };
  const handleClose = () => {
    setAlertData({ isShow: false });
  };

  const fields = useMemo(
    () => [
      { tab: "summary", label: "Project ID", value: lead?.id, icon: IdCard },
      {
        tab: "summary",
        label: "Invoice ID",
        value: lead?.invoiceId,
        icon: FileText,
      },
      {
        tab: "summary",
        label: "Cost",
        value: `$${lead?.cost} - ${lead?.payment}`,
        icon: Briefcase,
      },

      {
        tab: "summary",
        label: "User Name",
        value: lead?.customer?.name,
        icon: User,
        copyable: true,
      },
      {
        tab: "summary",
        label: "User Email",
        value: lead?.customer?.email,
        icon: Mail,
        copyable: true,
        link: `mailto:${lead?.customer?.email}`,
      },
      {
        tab: "summary",
        label: "User Phone",
        value: lead?.customer?.phone,
        icon: Phone,
        copyable: true,
        link: `tel:${lead?.customer?.phone}`,
      },

      {
        tab: "summary",
        label: "Agent",
        value: lead?.agent?.name,
        icon: UserCircle2,
      },
      {
        tab: "summary",
        label: "Support",
        value: lead?.support?.name,
        icon: UserCircle2,
      },

      {
        tab: "summary",
        label: "Start Date",
        value: lead?.start ?? "-",
        icon: CalendarDays,
      },
      {
        tab: "summary",
        label: "End Date",
        value: lead?.end ?? "-",
        icon: CalendarDays,
      },

      // deal
      {
        tab: "deal",
        label: "ID",
        value: lead?.project_deals?.dealid ?? "-",
        icon: Tag,
      },
      {
        tab: "deal",
        label: "Name",
        value: lead?.project_deals?.name ?? "-",
        icon: FileText,
      },
      {
        tab: "deal",
        label: "Product",
        value: lead?.project_deals?.product?.products ?? "-",
        icon: ShoppingBag,
      },
      {
        tab: "deal",
        label: "Package",
        value: lead?.package_details?.packagename ?? "-",
        icon: Package,
      },
      {
        tab: "deal",
        label: "Time",
        value: lead?.project_deals?.dealtime ?? "-",
        icon: Clock,
      },
    ],
    [lead, status]
  );

  const filteredFields = fields.filter(
    (f) =>
      (!query ||
        `${f.label} ${f.value}`.toLowerCase().includes(query.toLowerCase())) &&
      (activeTab === "summary"
        ? ["summary", "work", "personal"].includes(f.tab)
        : f.tab === activeTab)
  );

  useEffect(() => {
    GetData(data?.projectkey);
  }, [data]);

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[60] flex items-stretch justify-end bg-slate-900/40 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.aside
              ref={containerRef}
              initial={{ x: 40, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: 40, opacity: 0 }}
              transition={{ type: "spring", stiffness: 380, damping: 32 }}
              className="relative mt-auto mr-[2vh] mb-auto ml-0 flex h-[96vh] w-[800px] max-w-[98vw] flex-col overflow-hidden rounded-3xl border border-solid border-slate-200 bg-gradient-to-b from-white to-slate-50 shadow-xl"
              aria-modal
              role="dialog"
            >
              {isLoading ? (
                <>
                  {/* Header */}
                  <div className="border-b border-solid border-slate-200 bg-white/90 p-4 backdrop-blur supports-[backdrop-filter]:bg-white/70 w-full">
                    <div className="flex flex-wrap items-center justify-between gap-3 w-full">
                      <div className="flex min-w-0 items-center gap-3">
                        <div className="flex items-center justify-center size-12 bg-slate-200 rounded-full overflow-hidden">
                          <p className="capitalize font-semi-bold text-base text-black">
                            {lead?.customer?.name
                              .split(" ")
                              ?.map((n, i) => n.charAt(0))}
                          </p>
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <h2 className="truncate text-lg font-semibold tracking-tight text-slate-900">
                              {lead?.customer?.name}
                            </h2>
                            <Badge
                              tone={status === "approved" ? "green" : "blue"}
                            >
                              {status}
                            </Badge>
                          </div>
                          <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-slate-500">
                            <span className="inline-flex items-center gap-1">
                              <MailCheck className="size-4" />{" "}
                              {lead?.customer?.email}
                            </span>

                            <span className="inline-flex items-center gap-1">
                              <PhoneCall className="size-4" />{" "}
                              {lead?.customer?.phone}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          className="inline-flex items-center gap-1.5 rounded-xl border border-solid border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                          onClick={() => {
                            if (lead?.invoice_details) {
                              setInvoicePopup(true);
                              setInvoiceData(lead?.invoice_details);
                            }
                          }}
                          aria-label="Close"
                        >
                          <FileText className="size-4" /> View Invoice
                        </button>

                        <button
                          className="inline-flex items-center gap-1.5 rounded-xl bg-[#0B54FF] px-3 py-2 text-sm font-medium text-white hover:bg-[#0849e0]"
                          onClick={onClose}
                          aria-label="Close"
                        >
                          <X className="size-4" /> Exit
                        </button>
                      </div>
                    </div>

                    {/* Quick actions */}
                    <div className="mt-4 flex flex-wrap items-center gap-2">
                      <QuickAction
                        icon={Mail}
                        label="Email"
                        href={`mailto:${lead?.customer?.email}`}
                      />
                      <QuickAction
                        icon={Phone}
                        label="Call"
                        href={`tel:${lead?.customer?.phone}`}
                      />
                      <div className="ml-auto flex items-center gap-2">
                        <kbd className="rounded-md border border-solid bg-white px-1.5 py-1 text-[10px] text-slate-500">
                          ⌘/Ctrl
                        </kbd>
                        <span className="text-[10px] text-slate-500">
                          + F to search
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Body */}
                  <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 overflow-hidden p-4 w-full">
                    {/* Left: main content */}
                    <div className="col-span-12 flex min-h-0 flex-col gap-4 lg:col-span-12">
                      {/* Top stats */}
                      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                        {lead?.stats?.map((ele, idx) => {
                          return (
                            <Stat
                              title={ele?.key}
                              value={ele?.value ?? "-"}
                              icon={CalendarDays}
                            />
                          );
                        })}
                      </div>

                      {/* Tabs */}
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex flex-wrap items-center gap-1">
                          {tabs?.map((t) => (
                            <button
                              key={t.key}
                              onClick={() => setActiveTab(t.key)}
                              className={classNames(
                                "rounded-md px-[12px] py-[8px] text-sm font-medium border border-solid border-[#E1E4EA]",
                                activeTab === t.key
                                  ? "bg-[#F5F5F5] text-[#0E121B]"
                                  : "text-slate-600 hover:bg-slate-100"
                              )}
                            >
                              {t.label}
                            </button>
                          ))}
                        </div>
                        <div className="relative">
                          <Search className="pointer-events-none absolute left-3 top-2.5 size-4 text-slate-400" />
                          <input
                            id="quickview-search"
                            placeholder="Search fields…"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            className="w-64 rounded-2xl border border-solid border-slate-200 bg-white py-2 pl-9 pr-3 text-sm outline-none placeholder:text-slate-400 focus:ring-2 focus:ring-slate-900/10"
                          />
                        </div>
                      </div>

                      <div className="h-full overflow-auto rounded-2xl py-2">
                        {/* Render fields for current tab (or summary) */}
                        <div className="grid grid-cols-1 gap-3">
                          {filteredFields?.map((f, idx) => (
                            <Field key={idx} {...f} />
                          ))}
                        </div>

                        {activeTab === "transactions" && (
                          <div className="space-y-3">
                            {lead?.transactions?.map((txn) => (
                              <Field
                                key={txn?.transactionid}
                                label={txn?.transactiontime}
                                value={`$${txn?.amount}`}
                                icon={Briefcase}
                                // label2={deal?.amount}
                                endTag={true}
                                LastElement={() => (
                                  <Chip
                                    color={
                                      txn.transactionstatus == "success"
                                        ? "green"
                                        : "red"
                                    }
                                  >
                                    {txn.transactionstatus}
                                  </Chip>
                                )}
                              />
                            ))}
                          </div>
                        )}

                        {activeTab === "comments" && (
                          <>
                            {console.log(lead?.id)}
                            <CommentsSection
                              type="project"
                              id={lead?.id}
                              apiBaseUrl="project"
                              setAlertData={setAlertData}
                            />
                          </>
                        )}

                        {activeTab === "call" && (
                          <TabPlaceholder
                            icon={PhoneCall}
                            title="Calls"
                            message="We are working on the call logs and recordings section."
                          />
                        )}

                        {activeTab === "emails" && (
                          <TabPlaceholder
                            icon={Mail}
                            title="Emails"
                            message="Email activity and history will be available soon."
                          />
                        )}

                        {activeTab === "sms" && (
                          <TabPlaceholder
                            icon={MessageSquare}
                            title="SMS"
                            message="SMS communication history is under development."
                          />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="flex items-center justify-between gap-2 border-t border-solid border-slate-200 bg-white p-3 w-full">
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <IdCard className="size-4" /> ID {lead?.id}
                    </div>
                  </div>
                </>
              ) : (
                <InnerLoader wrapperClass={"w-full"} />
              )}
            </motion.aside>
          </motion.div>
        )}
      </AnimatePresence>

      {invoicePopup && invoiceData && (
        <InvoiceQuickView
          onClose={() => {
            setInvoicePopup(false);
            setInvoiceData(null);
          }}
          data={invoiceData}
          type="project"
        />
      )}

      <Snackbar
        open={alertData?.isShow}
        autoHideDuration={3000}
        onClose={handleClose}
      >
        <Alert
          onClose={handleClose}
          severity={alertData?.type}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {alertData?.message}
        </Alert>
      </Snackbar>
    </>
  );
}

function QuickAction({ icon: Icon, label, href, onClick }) {
  const className =
    "inline-flex items-center gap-1.5 rounded-xl border border-solid border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50";
  if (href)
    return (
      <a className={className} href={href}>
        <Icon className="size-4" /> {label}
      </a>
    );
  return (
    <button className={className} onClick={onClick}>
      <Icon className="size-4" /> {label}
    </button>
  );
}

function TabPlaceholder({ icon: Icon, title, message }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center border border-dashed border-slate-300 rounded-2xl bg-white">
      <div className="flex items-center justify-center size-14 rounded-full bg-slate-100 mb-4">
        <Icon className="size-6 text-slate-500" />
      </div>
      <h3 className="text-lg font-semibold text-slate-700">{title}</h3>
      <p className="text-sm text-slate-500 mt-1">{message}</p>
      <div className="mt-4 px-4 py-2 text-xs text-slate-400 border border-solid border-slate-200 rounded-full bg-slate-50">
        🚧 Under Development
      </div>
    </div>
  );
}

function Activity({ items, compact = false }) {
  return (
    <ul className="space-y-3">
      {items?.map((a, i) => (
        <li key={i} className="flex items-start gap-3">
          <div className="mt-1 h-5 w-5 rounded-full bg-slate-100 ring-1 ring-slate-200" />
          <div className="min-w-0 flex-1">
            <div className="text-[13px] text-slate-800 pb-[5px]">{a.text}</div>
            <div className="text-[11px] text-slate-400">{formatTs(a.ts)}</div>
          </div>
        </li>
      ))}
      {!compact && (
        <button className="mt-1 inline-flex items-center gap-1 text-xs text-slate-600 hover:underline">
          View full activity <ChevronRight className="size-4" />
        </button>
      )}
    </ul>
  );
}

function Assets({ assets }) {
  return (
    <div className="space-y-3">
      {assets?.map((a, i) => (
        <div
          key={i}
          className="flex items-center justify-between rounded-xl border border-solid border-slate-200 bg-white p-3"
        >
          <div className="flex min-w-0 items-center gap-3">
            <Monitor className="size-5" />
            <div className="min-w-0">
              <div className="truncate text-sm font-medium">{a.name}</div>
              <div className="truncate text-xs text-slate-500">{a.id}</div>
            </div>
          </div>
          <Badge tone={a.status === "Assigned" ? "blue" : "grey"}>
            {a.status}
          </Badge>
        </div>
      ))}
      <button className="inline-flex items-center gap-1 text-xs text-slate-600 hover:underline">
        Manage assets <ChevronRight className="size-4" />
      </button>
    </div>
  );
}

function Notes() {
  const [notes, setNotes] = useState([
    {
      id: 1,
      text: "Great initiative on latest branding sprint. Keep momentum for Q4.",
      ts: Date.now() - 86400000,
    },
  ]);
  const [text, setText] = useState("");
  return (
    <div className="space-y-3">
      <div className="rounded-2xl border border-solid border-slate-200 bg-white p-3">
        <textarea
          id="addNotesField"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Add a manager note (visible to HR/Managers)…"
          className="min-h-[90px] w-full resize-y rounded-xl border border-solid border-slate-200 bg-slate-50 p-3 text-sm outline-none focus:ring-2 focus:ring-slate-900/10"
        />
        <div className="mt-2 flex justify-end">
          <button
            onClick={() => {
              if (!text.trim()) return;
              setNotes((n) => [{ id: Date.now(), text, ts: Date.now() }, ...n]);
              setText("");
            }}
            className="rounded-xl bg-slate-900 px-3 py-1.5 text-xs text-white hover:bg-slate-800"
          >
            Save Note
          </button>
        </div>
      </div>
      <ul className="space-y-2">
        {notes?.map((n) => (
          <li
            key={n.id}
            className="rounded-xl border border-solid border-slate-200 bg-white p-3"
          >
            <div className="text-sm text-slate-800">{n.text}</div>
            <div className="text-[11px] text-slate-400">
              {new Date(n.ts).toLocaleString()}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="rounded-xl border border-solid border-slate-200 bg-slate-50 p-3 text-center">
      <div className="text-[11px] text-slate-500">{label}</div>
      <div className="text-sm font-semibold text-slate-800">{value}</div>
    </div>
  );
}

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

function formatTs(ts) {
  try {
    const d =
      typeof ts === "string" ? new Date(ts.replace(" ", "T")) : new Date(ts);
    return d.toLocaleString();
  } catch {
    return String(ts);
  }
}

function capitalize(s) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
